import { computed, shallowRef, watch } from "vue";
import type { useNxSocket } from "@blpde/connect";
import {
  matchesConnectNotificationFilter,
  type ConnectNotificationChannel,
  type ConnectNotificationFilter,
} from "@blpde/connect/config";
import { fetchLoadedRange } from "@/pagedRange";
import {
  attachNotificationConsumer,
  bellHasMore,
  bellNotifications,
  browserSettingOf,
  fetchNotificationPage,
  markNotificationRead,
  markNotificationsRead,
  notificationTypeRegister,
  onNotificationsDropped,
  snoozeNotification,
  type MasterNotification,
  type NotificationPageQuery,
} from "@/notifications";

/** master caps a page at 200 rows. */
const PAGE_SIZE = 200;
/** The most rows one subscription reads beyond the bell's list. */
const MAX_ROWS = 2000;

/**
 * The apps' channel to master's notifications (I-2915, `@blpde/connect`
 * `connectNotificationsKey`), filled from the shell's one store
 * (`notifications.ts`). Provided app-wide by `App.vue`.
 *
 * - **What a subscription holds** is what the bell holds of one module (and
 *   its types): unread, due, of the session's company or of none — master
 *   filtered it for this session and device type — and not `off` in this
 *   browser (`browserSettingOf`, the store's second guard for a change
 *   master learns late). Oldest first. Whatever takes a row out of the bell
 *   — read, snoozed, resolved or deleted here or elsewhere, a push of
 *   `notification.read` / `update` / `delete` — takes it out of the
 *   subscription too.
 * - **Rows beyond the bell's pages:** the bell lists the newest unread
 *   notifications page by page, so an older row of the subscription's
 *   module can lie past what it holds. While master has more unread than
 *   the bell lists, the subscription reads its own rows from master (by
 *   type, or by module) in every page and holds them with the bell's. It
 *   reads them again whenever the bell's list changes — every push, read
 *   and poll reloads it — and drops a row the bell drops at once.
 * - **A subscription keeps the store running** (its long poll and master's
 *   push lane) like the bell does, so the list holds while the shell's
 *   chrome is hidden.
 * - **The actions** are the store's: a read or a snooze drops the row from
 *   the bell at once and reaches every device through master.
 */
export function createNotificationChannel(
  socket: ReturnType<typeof useNxSocket>,
): ConnectNotificationChannel {
  return {
    subscribe(filter) {
      const detach = attachNotificationConsumer(socket);
      // The subscription's unread rows that lie past the bell's pages.
      const beyond = shallowRef<readonly MasterNotification[]>([]);
      const dropped = new Set<number>();
      let stopped = false;
      let running = false;
      let again = false;

      async function refresh(): Promise<void> {
        if (!bellHasMore.value) {
          // The bell holds every unread notification.
          beyond.value = [];
          return;
        }
        if (running) {
          again = true;
          return;
        }
        running = true;
        try {
          do {
            again = false;
            dropped.clear();
            let rows: MasterNotification[] | null;
            try {
              rows = await fetchFilterRows(filter);
            } catch {
              // The next change of the bell reads them again.
              rows = null;
            }
            if (stopped) return;
            if (rows) {
              beyond.value = rows.filter(
                (row) => !row.readAt && !dropped.has(row.id),
              );
            }
          } while (again);
        } finally {
          running = false;
        }
      }

      const stopWatch = watch(
        [bellNotifications, bellHasMore],
        () => void refresh(),
        {
          immediate: true,
        },
      );
      const stopDrops = onNotificationsDropped((ids) => {
        for (const id of ids) dropped.add(id);
        const gone = new Set(ids);
        if (beyond.value.some((row) => gone.has(row.id))) {
          beyond.value = beyond.value.filter((row) => !gone.has(row.id));
        }
      });
      const items = computed(() => {
        const seen = new Set<number>();
        return [...bellNotifications.value, ...beyond.value]
          .filter((row) => {
            if (seen.has(row.id)) return false;
            seen.add(row.id);
            return (
              matchesConnectNotificationFilter(row, filter) &&
              browserSettingOf(row) !== "off"
            );
          })
          .sort(byNotifyAt);
      });
      return {
        items,
        stop: () => {
          stopped = true;
          stopWatch();
          stopDrops();
          detach();
        },
      };
    },
    async markRead(ids) {
      const unique = [...new Set(ids)].filter((id) => id > 0);
      if (!unique.length) return true;
      try {
        if (unique.length === 1) await markNotificationRead(unique[0]);
        else await markNotificationsRead(unique);
        return true;
      } catch {
        return false;
      }
    },
    snooze: (id, until) =>
      snoozeNotification(id, until).then(
        () => true,
        () => false,
      ),
  };
}

/** Oldest first: the order the rows became visible. */
function byNotifyAt(a: MasterNotification, b: MasterNotification): number {
  const at = Date.parse(a.notifyAt || a.createdAt || "");
  const bt = Date.parse(b.notifyAt || b.createdAt || "");
  if (Number.isFinite(at) && Number.isFinite(bt) && at !== bt) return at - bt;
  return a.id - b.id;
}

/**
 * Every unread row of a filter, read from master: one query per type, or one
 * for the module when the filter names no type.
 */
async function fetchFilterRows(
  filter: ConnectNotificationFilter,
): Promise<MasterNotification[]> {
  const queries: NotificationPageQuery[] = [];
  if (filter.types?.length) {
    for (const type of filter.types) queries.push({ type });
  } else {
    const register = await notificationTypeRegister();
    const moduleType = [...register.values()].find(
      (type) => type.moduleKey === filter.moduleKey,
    );
    if (moduleType) queries.push({ moduleID: moduleType.moduleID });
  }
  const rows = new Map<number, MasterNotification>();
  for (const query of queries) {
    const { rows: got } = await fetchLoadedRange<MasterNotification>(
      MAX_ROWS,
      PAGE_SIZE,
      async (limit, after) =>
        (
          await fetchNotificationPage({
            ...query,
            unread: true,
            limit,
            after,
          })
        ).notifications,
    );
    for (const row of got) {
      if (matchesConnectNotificationFilter(row, filter)) rows.set(row.id, row);
    }
  }
  return [...rows.values()];
}
